import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO, isBefore, startOfDay } from 'date-fns';
import './Calendar.css';

const Calendar = ({ events = [], onDateClick }) => {
    const today = startOfDay(new Date());
    const [currentDate, setCurrentDate] = useState(new Date());

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const startDate = monthStart;
    const startDay = startDate.getDay();
    const prevMonthDays = [];

    for (let i = startDay - 1; i >= 0; i--) {
        const prevDate = new Date(startDate);
        prevDate.setDate(startDate.getDate() - (i + 1));
        prevMonthDays.push(prevDate);
    }

    const endDate = monthEnd;
    const endDay = endDate.getDay();
    const nextMonthDays = [];

    for (let i = 1; i <= (6 - endDay); i++) {
        const nextDate = new Date(endDate);
        nextDate.setDate(endDate.getDate() + i);
        nextMonthDays.push(nextDate);
    }

    const allDays = [...prevMonthDays, ...daysInMonth, ...nextMonthDays];
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
        weeks.push(allDays.slice(i, i + 7));
    }

    const getEventsForDate = (date) => {
        return events.filter(event => {
            try {
                let eventDate;
                if (typeof event.date === 'string') {
                    if (/^\d+$/.test(event.date)) {
                        eventDate = new Date(parseInt(event.date));
                    } else {
                        eventDate = parseISO(event.date);
                    }
                } else if (typeof event.date === 'number') {
                    eventDate = new Date(event.date);
                } else {
                    eventDate = new Date(event.date);
                }
                return isSameDay(eventDate, date);
            } catch (error) {
                console.error('Error parsing event date:', event.date, error);
                return false;
            }
        });
    };

    const handleDateClick = (date, dayEvents) => {
        if (dayEvents.length > 0 && onDateClick) {
            onDateClick(date, dayEvents);
        }
    };

    const isViewingCurrentMonth = isSameMonth(currentDate, today);

    const navigateMonth = (direction) => {
        if (direction === 'prev' && !isViewingCurrentMonth) {
            setCurrentDate(subMonths(currentDate, 1));
        } else if (direction === 'next') {
            setCurrentDate(addMonths(currentDate, 1));
        }
    };

    return (
        <>
            <h4 className="calendar-description">Refer calendar for other events and miqaats</h4>
            <div className="calendar-container">
                <div className="calendar-header">
                    <button
                        className={`calendar-nav-btn ${isViewingCurrentMonth ? 'disabled' : ''}`}
                        onClick={() => navigateMonth('prev')}
                        aria-label="Previous month"
                        disabled={isViewingCurrentMonth}
                    >
                        ‹
                    </button>
                    <h3 className="calendar-month-year">
                        {format(currentDate, 'MMMM yyyy')}
                    </h3>
                    <button
                        className="calendar-nav-btn"
                        onClick={() => navigateMonth('next')}
                        aria-label="Next month"
                    >
                        ›
                    </button>
                </div>
                <div className="calendar-grid">
                    <div className="calendar-weekdays">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="calendar-weekday">{day}</div>
                        ))}
                    </div>
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="calendar-week">
                            {week.map((day, dayIndex) => {
                                const dayEvents = getEventsForDate(day);
                                const isDayInCurrentMonth = isSameMonth(day, currentDate);
                                const isToday = isSameDay(day, today);
                                const isPastDay = isBefore(startOfDay(day), today);
                                const hasEvents = dayEvents.length > 0;
                                const hasFutureEvents = hasEvents && !isPastDay;
                                const hasMiqaat = hasFutureEvents && dayEvents.some(event => !event.venue && !event.eventType);
                                const hasTasbeeh = hasFutureEvents && dayEvents.some(event => event.category?.toLowerCase() === 'tasbeeh');
                                const hasRegularEvent = hasFutureEvents && dayEvents.some(event => event.venue || event.eventType);
                                const isClickable = hasFutureEvents;
                                return (
                                    <div
                                        key={dayIndex}
                                        className={`calendar-day ${!isDayInCurrentMonth ? 'other-month' : ''} ${isPastDay ? 'past-day' : ''} ${isToday ? 'today' : ''} ${hasFutureEvents ? 'has-events' : ''} ${hasTasbeeh ? 'has-tasbeeh' : hasMiqaat ? 'has-miqaat' : hasRegularEvent ? 'has-regular-event' : ''} ${isClickable ? 'clickable' : 'not-clickable'}`}
                                        onClick={() => isClickable && handleDateClick(day, dayEvents)}
                                    >
                                        <span className="day-number">{format(day, 'd')}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default Calendar;
